import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setError("A valid email, so the reset can find you.");
      return;
    }
    setError("");
    setStatus("sending");
    try {
      await api.post("/subscribe", { email });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="reset" className="relative py-28 md:py-36" data-testid="reset-section">
      <div className="max-w-4xl mx-auto px-6">
        <Reveal>
          <div className="relative bg-sand/50 border border-[rgba(169,129,47,0.26)] px-8 md:px-16 py-16 md:py-20 text-center overflow-hidden">
            <div
              className="absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[280px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(ellipse, rgba(169,129,47,0.135), transparent 65%)" }}
              aria-hidden="true"
            />
            <p className="eyebrow mb-6">A free practice</p>
            <h2 className="font-display text-ink font-medium leading-[1.12] text-3xl sm:text-4xl md:text-5xl" data-testid="reset-heading">
              The 7-Minute Nervous System Reset
            </h2>
            <p className="mt-6 font-display italic text-goldink text-xl md:text-2xl leading-relaxed" data-testid="reset-tagline">
              Before the meeting. Before the reply. Before you react — seven minutes.
            </p>
            <p className="mt-5 text-inkdim text-sm leading-relaxed max-w-md mx-auto">
              A short grounding practice that brings the body down from the ceiling —
              so the next move comes from the pattern you chose, not the one that chose you.
            </p>

            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="ok"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-10 flex flex-col items-center gap-3"
                  data-testid="reset-success-message"
                >
                  <span className="inline-flex w-10 h-10 rounded-full border border-gold/50 items-center justify-center text-gold">
                    <Check size={16} strokeWidth={1.5} />
                  </span>
                  <p className="font-display text-xl text-goldink">Received. Keep an eye on your inbox.</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  noValidate
                  exit={{ opacity: 0, y: -8 }}
                  className="mt-10 flex flex-col sm:flex-row items-stretch gap-4 max-w-lg mx-auto"
                  data-testid="reset-form"
                >
                  <div className="flex-1 text-left">
                    <label htmlFor="reset-email" className="sr-only">Email address</label>
                    <input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full bg-transparent border-b border-[rgba(169,129,47,0.26)] focus:border-gold py-3.5 text-ink placeholder:text-inkdim/40 outline-none transition-colors duration-300 text-[15px]"
                      data-testid="reset-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-8 py-3.5 hover:bg-gold transition-colors duration-300 disabled:opacity-60 disabled:cursor-wait shrink-0"
                    data-testid="reset-submit-button"
                  >
                    {status === "sending" ? "Sending…" : "Send me the reset"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
            {error && <p className="text-xs text-[#9E3B1F] mt-3" data-testid="reset-error-message">{error}</p>}
            {status === "error" && (
              <p className="text-sm text-[#9E3B1F] mt-3" data-testid="reset-send-error">
                The send didn't land. Please try once more.
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
